import { DateTime } from 'luxon'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import Button from '../../../../components/atoms/button/Button'
import Loader from '../../../../components/atoms/loader/Loader'
import Text from '../../../../components/atoms/text/Text'
import DatePicker from '../../../../components/molecules/datePicker/DatePicker'
import ListItem from '../../../../components/molecules/listItem/ListItem'
import Screen from '../../../../components/molecules/screen/Screen'
import routes from '../../../../config/routes'
import { useProfile } from '../../../../modules/auth/hooks'
import {
  useHolidays,
  useReservatedSeatById,
  useSeatById,
  useUpdateReservateSeatMutation,
} from '../../../../modules/seats/hooks'
import { getBookSeatType, getBookShift } from '../../../../modules/seats/utils'
import { usePurchasedVoucher } from '../../../../modules/vouchers/hooks'
import { SHIFTS } from '../../../../providers/bookAssistant/shifts'
import { useCardTransition } from '../../../../providers/cardTransition/cardTransition'
import { VARIANTS } from '../../../../types/Components.types'
import styles from './BookChangeDateScreen.module.css'

const BookChangeDateScreen = () => {
  const { bookId } = useParams() as { bookId: string }
  const [selectedDate, setSelectedDate] = useState<Date | null>(null)
  const [queryParams, setQueryParams] = useState<URLSearchParams>()
  const { data: profile, isLoading: isLoadingProfile } = useProfile()
  const { data: reservation, isLoading: isLoadingReservation } = useReservatedSeatById({
    userId: profile?.id,
    seatId: bookId,
  })
  const { data: voucher, isLoading: isLoadingVoucher } = usePurchasedVoucher({
    userId: profile?.id,
    voucherId: reservation?.seat.userVoucher,
  })
  const { data: seat, isLoading: isLoadingSeat } = useSeatById({ seatId: reservation?.seat.SeatId, queryParams })
  const { data: holidays } = useHolidays()

  const isLoading = isLoadingProfile || isLoadingReservation || isLoadingVoucher

  const navigate = useNavigate()
  const { openCard } = useCardTransition()

  const updateReservatedSeat = useUpdateReservateSeatMutation()

  const currentDate = useMemo(() => {
    if (!reservation) return null
    return DateTime.fromISO(reservation.seat.reserved_from)
  }, [reservation])

  const seatAvailable = useMemo(() => {
    if (!selectedDate || !seat || isLoadingSeat) return null
    return !seat.disabled
  }, [selectedDate, seat, isLoadingSeat])

  const handleDateConfirm = useCallback((date: Date | Date[]) => {
    setSelectedDate(Array.isArray(date) ? date[0] : date)
  }, [])

  const handleDateSelectorClick = useCallback(() => {
    openCard?.({
      title: 'Elige la nueva fecha',
      Component: (
        <DatePicker
          initialDate={selectedDate}
          onDateConfirm={handleDateConfirm}
          disabledDays={[0, 6]}
          disabledDates={[...holidays, currentDate ? currentDate.toJSDate() : null].filter(Boolean) as Date[]}
        />
      ),
    })
  }, [openCard, selectedDate, handleDateConfirm, holidays, currentDate])

  const handleContinueClick = useCallback(() => {
    if (!selectedDate || !seatAvailable) return

    updateReservatedSeat.mutate(
      {
        userId: profile!.id,
        date: selectedDate,
        seatId: reservation!.seat.SeatId,
        bookId,
      },
      {
        onSuccess: () => {
          navigate(routes.book.list)
        },
      },
    )
  }, [updateReservatedSeat, profile, selectedDate, seatAvailable, reservation, bookId, navigate])

  useEffect(() => {
    if (!voucher || !selectedDate) {
      return
    }

    const params: {
      from: string
      to: string
      type: string
      date: string
      seatType?: string
    } = {
      type: 'days',
      date: DateTime.fromJSDate(selectedDate).toISODate()!,
      from: String(getBookShift({ voucher })?.from ?? SHIFTS.morning.from),
      to: String(getBookShift({ voucher })?.to ?? SHIFTS.morning.to),
      seatType: getBookSeatType({ voucher }) ?? '',
    }

    setQueryParams(new URLSearchParams(params))
  }, [voucher, selectedDate])

  useEffect(() => {
    if (!isLoading && !reservation) {
      navigate(-1)
      return
    }
  }, [navigate, isLoading, reservation])

  const canConfirm = selectedDate && seatAvailable === true && !updateReservatedSeat.isPending

  return (
    <Screen
      headerProps={{
        backEnabled: true,
        variant: VARIANTS.flamingo,
        title: 'Cambiar fecha',
      }}
    >
      <div className={styles.container}>
        <Text size="l" variant={VARIANTS.primary} weight="700" className={styles.title}>
          Selecciona la nueva fecha para tu reserva
        </Text>

        <Text className={styles.subtitle}>
          Tu reserva actual es para el día{' '}
          <Text element="span" weight="600">
            {currentDate?.toLocaleString(DateTime.DATE_SHORT)}
          </Text>
        </Text>

        <div className={styles.content}>
          <ListItem label="Nueva fecha">
            <Button
              className={styles.button}
              autoSize
              variant={VARIANTS.secondary}
              inverted
              onClick={handleDateSelectorClick}
            >
              <Text variant={VARIANTS.secondary} weight="500">
                {selectedDate ? DateTime.fromJSDate(selectedDate).toLocaleString(DateTime.DATE_SHORT) : 'Seleccionar'}
              </Text>
            </Button>
          </ListItem>

          {selectedDate && isLoadingSeat && (
            <div className={styles.loadingContainer}>
              <Loader variant="primary" />
              <Text size="s" className={styles.loadingText}>
                Verificando disponibilidad...
              </Text>
            </div>
          )}

          {selectedDate && !isLoadingSeat && seatAvailable === false && (
            <div className={styles.errorContainer}>
              <Text variant={VARIANTS.negative} size="s">
                Lo sentimos, tu sitio {reservation?.name} no está disponible para la fecha seleccionada. Por favor,
                elige otra fecha.
              </Text>
            </div>
          )}

          {selectedDate && !isLoadingSeat && seatAvailable === true && (
            <div className={styles.successContainer}>
              <Text variant={VARIANTS.primary} size="s" weight="600">
                ¡Tu sitio {reservation?.name} está disponible para esta fecha!
              </Text>
            </div>
          )}
        </div>

        <div className={styles.actions}>
          <Button
            variant={VARIANTS.primary}
            className={styles.action}
            onClick={handleContinueClick}
            disabled={!canConfirm}
            isLoading={updateReservatedSeat.isPending}
          >
            Confirmar cambio
          </Button>
        </div>
      </div>
    </Screen>
  )
}

export default React.memo(BookChangeDateScreen)
